import {
  Bookmark,
  ListPlus,
  ThumbsUp
} from '@phosphor-icons/react'
import React, { useState } from 'react'
import { useFormFactor, useGroup } from '../../../hooks'
import { useDisplayAddToPlaylist } from '../../../hooks/useDisplayAddToPlaylist'
import { ALL_CONSUMER } from '../../../services/cognito/userGroups'
import {
  ActionButton,
  AddToPlaylistButton,
  BookmarkButton,
  LikeButton,
  MoreActions,
  ToggleDescriptionButton
} from '../../action-buttons'
import { UserGroupPolicy } from '../../user-group-policy'
import { ALLOW_PLAYLISTS_GROUP } from '../../user-group-policy/permissions'
import GeneralCard from '../general-card'

const HomeCard = ({
  content,
  testId,
  onAddToPlaylist,
  onBookmark,
  size,
  bookmarked,
  onLike,
  actionLoading,
  ...props
}) => {
  const { isDesktop, isLargeTablet } = useFormFactor()
  const cardSize = size || (isDesktop ? 'large' : 'mobile')
  const isFullWidth = isDesktop || isLargeTablet
  // MT.3: description is always visible (no hover reveal). `expanded`
  // starts true regardless of viewport; mobile can still toggle it via
  // ToggleDescriptionButton if a user wants to collapse the subtitle.
  const [expanded, setExpanded] = useState(true)
  const [hoverShadow, setHoverShadow] = useState(false)
  const { isInGroup } = useGroup()
  const isConsumer = isInGroup(ALL_CONSUMER)
  const displayAddToPlaylist = useDisplayAddToPlaylist()

  const handleToggleDescription = () => setExpanded(!expanded)
  const handleMouseEnter = () => setHoverShadow(true)
  const handleMouseLeave = () => setHoverShadow(false)

  const handleAddToPlaylist = () => {
    onAddToPlaylist(content)
  }

  const handleLike = () => onLike(content)

  let actions = {
    leftActions: (
      <>
        <LikeButton
          variant={isConsumer ? 'none' : 'secondary'}
          size='xs'
          label={content?.hasRecommend ? 'Liked' : 'Like'}
          startIcon={
            <ThumbsUp size={18} weight={content?.hasRecommend ? 'fill' : 'duotone'} />
          }
          onClick={handleLike}
          likes={content?.likeCount ?? 0}
          loading={actionLoading?.like}
          aria-busy={actionLoading?.like}
          aria-label={content?.hasRecommend ? 'Unlike content' : 'Like content'}
        />
        <BookmarkButton
          variant={isConsumer ? 'none' : 'secondary'}
          size='xs'
          bookmarked={bookmarked}
          onClick={onBookmark}
          startIcon={<Bookmark size={18} weight={bookmarked ? 'fill' : 'regular'} />}
          loading={actionLoading?.bookmark}
          aria-busy={actionLoading?.bookmark}
          aria-label={bookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
        />
        {displayAddToPlaylist && (
          <UserGroupPolicy
            accessGroup={ALLOW_PLAYLISTS_GROUP}
            componentAllow={
              <ActionButton
                variant={isConsumer ? 'none' : 'secondary'}
                size='xs'
                label='Add to Playlist'
                startIcon={<ListPlus size={18} weight='duotone' />}
                onClick={handleAddToPlaylist}
              />
            }
          />
        )}
      </>
    )
  }

  if (!isFullWidth) {
    actions = {
      leftActions: (
        <ToggleDescriptionButton
          size='xs'
          variant={isConsumer ? 'none' : 'secondary'}
          toggled={expanded}
          onClick={handleToggleDescription}
        />
      ),
      rightActions: (
        <>
          <MoreActions
            variant={isConsumer ? 'none' : 'secondary'}
            size='xs'
            loading={
              actionLoading && Object.values(actionLoading).some((val) => !!val)
            }
            actions={(actions) => [
              [
                <LikeButton
                  key='Like'
                  variant='none'
                  label={content?.hasRecommend ? 'Liked' : 'Like'}
                  loading={actionLoading?.like}
                  likes={content?.likeCount ?? 0}
                  onClick={() => {
                    handleLike()
                    actions.onClose()
                  }}
                  className={actions.className}
                  aria-busy={actionLoading?.like}
                  aria-label={content?.hasRecommend ? 'Unlike content' : 'Like content'}
                />
              ],
              [
                <BookmarkButton
                  key='Bookmark'
                  className={actions.className}
                  loading={actionLoading?.bookmark}
                  onClick={() => {
                    onBookmark()
                    actions.onClose()
                  }}
                  bookmarked={bookmarked}
                  aria-busy={actionLoading?.bookmark}
                  aria-label={bookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
                />
              ],
              ...(isInGroup(ALLOW_PLAYLISTS_GROUP) && displayAddToPlaylist
                ? [
                  <AddToPlaylistButton
                    key='AddToPlaylist'
                    className={actions.className}
                    onClick={() => {
                      handleAddToPlaylist()
                      actions.onClose()
                    }}
                    aria-label='Add this item to a playlist'
                  />
                  ]
                : [])
            ]}
          />
        </>
      )
    }
  }

  return (
    <GeneralCard
      fullWidth={isFullWidth}
      expand={expanded}
      size={cardSize}
      hoverShadow={hoverShadow}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      actions={actions}
      content={content}
      testId={testId}
      {...props}
    />
  )
}

export default HomeCard
